'use client';
import { useState } from 'react';
import { useConsent } from './ConsentProvider';

export function ConsentBanner() {
  const { showBanner, bannerMode, save } = useConsent();
  const isModal = bannerMode === 'modal';
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  // Seed toggles when Manage opens (banner is visible → mode is known):
  // strict/modal (opt-in) → OFF; open/notice (opt-out) → ON.
  const openManage = () => {
    const def = !isModal;
    setAnalytics(def);
    setMarketing(def);
    setManaging(true);
  };
  if (!showBanner || !bannerMode) return null;

  const text = (
    <p>
      We use cookies for analytics and marketing. You can accept all, reject all, or choose. See our{' '}
      <a href="/cookie-policy" className="underline">Cookie Policy</a>.
    </p>
  );

  const toggles = managing && (
    <div className="flex flex-wrap gap-4">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} /> Analytics
      </label>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} /> Marketing
      </label>
    </div>
  );

  const buttons = (
    <div className="flex flex-wrap gap-2">
      <button type="button" className="px-4 py-2 rounded bg-stone-800 text-white" onClick={() => save({ analytics: true, marketing: true })}>
        {isModal ? 'Accept all' : 'Got it'}
      </button>
      <button type="button" className="px-4 py-2 rounded bg-stone-200 text-stone-800" onClick={() => save({ analytics: false, marketing: false })}>
        Reject all
      </button>
      {managing ? (
        <button type="button" className="px-4 py-2 rounded border border-stone-300" onClick={() => save({ analytics, marketing })}>
          Save choices
        </button>
      ) : (
        <button type="button" className="px-4 py-2 rounded border border-stone-300" onClick={openManage}>
          {isModal ? 'Manage preferences' : 'Manage / opt out'}
        </button>
      )}
    </div>
  );

  if (isModal) {
    // Blocking modal for strict (EU/EEA/UK) regions. Reject is as easy as Accept
    // and still lets them use the site — not a "cookie wall".
    return (
      <div role="dialog" aria-modal="true" aria-label="Cookie consent" className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 text-sm text-stone-700">
          <h2 className="text-lg font-semibold text-stone-900 mb-2">Your privacy choices</h2>
          {text}
          <div className="my-3">{toggles}</div>
          {buttons}
        </div>
      </div>
    );
  }

  // Dismissible notice for open (default-on) regions.
  return (
    <div role="dialog" aria-label="Cookie notice" className="fixed bottom-0 inset-x-0 z-[200] bg-white border-t border-stone-200 shadow-2xl p-4 text-sm text-stone-700">
      <div className="max-w-5xl mx-auto flex flex-col gap-3">
        {text}
        {toggles}
        {buttons}
      </div>
    </div>
  );
}
